import { ExpandMore } from '@mui/icons-material';
import { Accordion, AccordionDetails, Typography, useEventCallback } from '@mui/material';
import { useFormik, useFormikContext } from 'formik';
import { PmiButton } from 'shell/components/shared/button';
import { DateTime } from 'luxon';
import { PracticeDetailVM } from 'practice/view-models/practice-detail';
// import { PracticeDetailVM } from 'practice/view-models/practice-detail';
import { Fragment, memo, useCallback, useEffect, useRef, useState } from 'react';
import { generatePath, useNavigate, useParams } from 'react-router-dom';
import { toast } from 'react-toastify';
import { AccordionSummary } from 'shell/components/shared/summary-accordion';
import { __PRACTICE_API_URL__ } from 'shell/constants/environment';
import { useHttpClient } from 'shell/hooks/use-http-fetch/stateless';
import { ResponsePromise } from 'shell/hooks/use-http-fetch/stateless/type';
import * as yup from 'yup';
import AccordionNegativeClosurePracticeForm, { CancellationPracticeVM } from './form';
import { TypeRegister } from 'practice/constants/type-register';
import { HttpResponse } from 'shell/models/http-response';
import { ResponseCertificateGeneric } from 'shell/models/response-certificate-generic';
import { transformResponseRequestCertificateGeneric } from 'front-office/utils/transform-response-certificate-generic';
import ModalWarningMessage from '../verification-messi/modal-warning';
import { blobToBase64 } from 'shell/utils/base64-to-blob';
import { ModalViewerPDF } from 'shell/components/shared/pdf-modal';
import ModalPrintSuccess from '../print-comunication/modal';
import { CodiceDominioPraticaTipo11 } from './practice-text';
import { ItemVM } from 'shell/view-models/item';
import { regexOnlyNumber } from 'shell/constants/regex';

interface NegativeClosureProps {
  loadUpdateDate: () => Promise<PracticeDetailVM> | void;
  practiceData?: PracticeDetailVM;
}

const AccordionNegativeClosurePracticeComponent = ({
  loadUpdateDate,
  practiceData,
}: NegativeClosureProps): React.ReactElement => {
  const [open, setOpen] = useState(true);

  const [hideReasonBox, setHideReasonBox] = useState(false);

  const ref = useRef<HTMLDivElement | null>(null);

  const params = useParams();

  const navigate = useNavigate();

  const [warningModal, setWarningModal] = useState<{ open: boolean; warnings?: string }>({
    open: false,
  });

  const [pdfModal, setPdfModal] = useState<{ open: boolean; data?: string; name?: string }>({
    data: '',
    open: false,
    name: '',
  });

  const [modalPrintSuccess, setModalPrintSuccess] = useState<{
    open: boolean;
    data?: string;
  }>({
    open: false,
  });

  const checkNotificationControl = useHttpClient(
    `${__PRACTICE_API_URL__}/chiusura-pratica/controlli-notifica`,
    {
      options: { method: 'POST' },
    }
  );

  const cancelNotifications = useHttpClient(
    `${__PRACTICE_API_URL__}/chiusura-pratica/cancella-notifica`,
    {
      options: { method: 'POST' },
    }
  );

  const printNotification = useHttpClient(
    `${__PRACTICE_API_URL__}/chiusura-pratica/stampa-notifica`,
    {
      options: { method: 'POST' },
    }
  );

  const formContextPage = useFormikContext<PracticeDetailVM>();

  const isCensus = params.tipoRegistro === TypeRegister.UntraceableCensus;

  useEffect(() => {
    if (
      params.tipoRegistro === TypeRegister.Unavailable ||
      params.tipoRegistro === TypeRegister.UntraceableCensus
    ) {
      setHideReasonBox(true);
    } else {
      setHideReasonBox(false);
    }
  }, [params.tipoRegistro]);

  const updateData = useHttpClient(`${__PRACTICE_API_URL__}/chiusura-pratica/esito`, {
    options: { method: 'POST' },
  });

  const printData = useHttpClient<
    HttpResponse<ResponseCertificateGeneric>,
    HttpResponse<ResponseCertificateGeneric>
  >(`${__PRACTICE_API_URL__}/pratiche/stampa/chiusura-negativa-pratica`, {
    options: { method: 'POST' },
    transformer: transformResponseRequestCertificateGeneric,
  });

  const printDataUnavailability = useHttpClient<Blob>(
    `${__PRACTICE_API_URL__}/pratiche/stampa/chiusura-negativa-procedimento`,
    {
      options: { method: 'POST' },
    }
  );

  const onPrint = useEventCallback((values: CancellationPracticeVM) => {
    if (
      !values.numberProtocol &&
      !values.observationDate &&
      values.acquisitionSystem?.id === CodiceDominioPraticaTipo11.OsservazioniNegative
    ) {
      formContext.handleSubmit();
      return;
    }
    const data = {
      codiceEnte: params.codiceEnte || undefined,
      tipoRegistro: params.tipoRegistro || undefined,
      idAnnoPratica: params.idAnnoPratica ? Number.parseInt(params.idAnnoPratica, 10) : undefined,
      progressivoPratica: params.progressivoPratica
        ? Number.parseInt(params.progressivoPratica, 10)
        : undefined,
      motivo: values.acquisitionSystem?.id || values.closureReason || undefined,
      dataOsservazioni: values.observationDate
        ? values.observationDate.toFormat('yyyy-MM-dd')
        : undefined,
      protocolloNr: values.numberProtocol || undefined,
    };

    (
      printData({ data }) as ResponsePromise<
        HttpResponse<ResponseCertificateGeneric>,
        HttpResponse<ResponseCertificateGeneric>
      >
    ).then((response) => {
      // print
      const responseCasted = response as HttpResponse<ResponseCertificateGeneric>;
      const pdfCertificate = responseCasted?.payload?.contenutoPdf;
      if (pdfCertificate) {
        // openPdf(pdfCertificate);
        setPdfModal({ data: pdfCertificate, open: true, name: 'Stampa chiusura negativa' });
      }
    });
  });

  const onPrintUnavailability = useEventCallback(() => {
    const data = {
      codiceEnte: params.codiceEnte || undefined,
      tipoRegistro: params.tipoRegistro || undefined,
      idAnnoPratica: params.idAnnoPratica ? Number.parseInt(params.idAnnoPratica, 10) : undefined,
      progressivoPratica: params.progressivoPratica
        ? Number.parseInt(params.progressivoPratica, 10)
        : undefined,
      dataPratica: practiceData?.practice?.dateOpen,
      telefono: practiceData?.movementType?.telephone,
      peo: practiceData?.movementType?.peo,
      pec: practiceData?.movementType?.pec,
    };

    (printDataUnavailability({ data, responseType: 'blob' }) as ResponsePromise<Blob>).then(
      (response) => {
        const pdfCertificate = response?.data;
        if (pdfCertificate) {
          const blob = new Blob([pdfCertificate], { type: 'application/pdf' });
          blobToBase64(blob).then((base64) => {
            setPdfModal({
              data: base64.split(',')[1],
              name: 'Stampa Dichiarazione',
              open: true,
            });
          });
        }
      }
    );
  });

  const onSubmit = useEventCallback((values: CancellationPracticeVM) => {
    const date = values.closureDate?.toFormat('yyyy-MM-dd') || '';
    const hours = values.closureTime?.toFormat('HH:mm:ss') || '';
    const timestampChiusura =
      DateTime.fromFormat(`${date || ''} ${hours || ''}`, 'yyyy-MM-dd HH:mm:ss') || undefined;

    const data = {
      codiceEnte: params.codiceEnte || undefined,
      tipoRegistro: params.tipoRegistro || undefined,
      idAnnoPratica: params.idAnnoPratica ? Number.parseInt(params.idAnnoPratica, 10) : undefined,
      progressivoPratica: params.progressivoPratica
        ? Number.parseInt(params.progressivoPratica, 10)
        : undefined,
      tsChiusura: timestampChiusura,
      motivoChiusura: values.closureReason || undefined,
      // dataOsservazioni: values.observationDate
      //   ? values.observationDate.toFormat('yyyy-MM-dd')
      //   : undefined,
      // protcolloNr: values.numberProtocol || undefined,
      esito: '0', // 0 se chiusura negativa 1 chiusura positiva
      chiusuraCensimento: isCensus
        ? {
            key: {
              codiceEnte: params.codiceEnte || undefined,
              tipoRegistro: params.tipoRegistro || undefined,
              idAnnoPratica: params.idAnnoPratica
                ? Number.parseInt(params.idAnnoPratica, 10)
                : undefined,
              progressivoPratica: params.progressivoPratica
                ? Number.parseInt(params.progressivoPratica, 10)
                : undefined,
            },
            annoCensimento: values.censusYear ? Number.parseInt(values.censusYear, 10) : undefined,
            servizioCensimento: values.censusService
              ? Number.parseInt(values.censusService, 10)
              : undefined,
            foglioCensimento: values.censusSheet
              ? Number.parseInt(values.censusSheet, 10)
              : undefined,
            dataVerifica: values.verificationDate
              ? values.verificationDate.toFormat('yyyy-MM-dd')
              : undefined,
            motivoCompilazione: values.censusReason?.id?.toString() || undefined,
          }
        : undefined,
    };

    (updateData({ data }) as ResponsePromise<void>).then(async () => {
      toast.success('La richiesta di chiusura negativa della pratica è stata registrata');

      // aggiornamento stato pratica
      formContextPage.setFieldValue('practice', {
        ...(formContextPage.values.practice || {}),
        codiceStatoPratica: '9',
        //tipoOperazione: '06',
      });

      // se cambia lo stato della pratica ricaricare dati
      const newData: PracticeDetailVM | void = await loadUpdateDate();

      if (newData) {
        formContextPage.setValues({
          ...(formContextPage.values || {}),
          ...newData,
        });
      }
    });

    navigate({
      pathname: generatePath(
        '/pratiche/ricerca/dettaglio/:codiceEnte/:tipoRegistro/:idAnnoPratica/:progressivoPratica/:idDbLocaleRichiedente/:idAnprIntestatario/:tipoPratica',
        {
          codiceEnte: params.codiceEnte,
          tipoRegistro: params.tipoRegistro,
          idAnnoPratica: params.idAnnoPratica?.toString(),
          progressivoPratica: params.progressivoPratica?.toString(),
          idDbLocaleRichiedente: params.idDbLocaleRichiedente?.toString() || '',
          idAnprIntestatario: params.idAnprIntestatario?.toString() || '',
          tipoPratica: params.tipoPratica,
        }
      ),
    });
  });

  useEffect(() => {
    ref.current?.scrollIntoView();
  }, []);

  const formContext = useFormik<CancellationPracticeVM>({
    initialValues: {
      closureDate: DateTime.now(),
      closureTime: DateTime.now(),
      closureReason: '',
      acquisitionSystem: null,
      numberProtocol: '',
      observationDate: null,
      censusYear:
        params.tipoRegistro === TypeRegister.UntraceableCensus
          ? (DateTime.now().year - 2).toString()
          : '',
      censusService: params.tipoRegistro === TypeRegister.UntraceableCensus ? '0' : '',
      censusSheet: params.tipoRegistro === TypeRegister.UntraceableCensus ? '0' : '',
      verificationDate: null,
      censusReason: null,
    },
    validationSchema: yup.object().shape({
      closureDate: yup
        .date()
        .typeError('Data non valida')
        .nullable()
        .required('Il campo è obbligatorio'),
      closureTime: yup
        .date()
        .typeError('Data non valida')
        .nullable()
        .required('Il campo è obbligatorio'),
      closureReason: hideReasonBox
        ? yup.string().nullable()
        : yup.string().nullable().required('Il campo è obbligatorio'),
      acquisitionSystem: yup.object().nullable(),
      numberProtocol: yup.string().when('acquisitionSystem', {
        is: (acquisitionSystem: ItemVM) =>
          acquisitionSystem?.id === CodiceDominioPraticaTipo11.OsservazioniNegative,
        then: yup.string().required('Campo obbligatorio'),
        otherwise: yup.string().nullable(),
      }),
      observationDate: yup
        .date()
        .when('acquisitionSystem', {
          is: (acquisitionSystem: ItemVM) =>
            acquisitionSystem?.id === CodiceDominioPraticaTipo11.OsservazioniNegative,
          then: yup.date().required('Campo obbligatorio').nullable(),
          otherwise: yup.date().nullable(),
        })
        .typeError('Data non valida'),
      censusYear: isCensus
        ? yup
            .string()
            .nullable()
            .matches(regexOnlyNumber, 'Campo numerico')
            .required('Il campo è obbligatorio')
        : yup.string().nullable(),
      censusService: isCensus
        ? yup
            .string()
            .nullable()
            .matches(regexOnlyNumber, 'Campo numerico')
            .required('Il campo è obbligatorio')
        : yup.string().nullable(),
      censusSheet: isCensus
        ? yup
            .string()
            .nullable()
            .matches(regexOnlyNumber, 'Campo numerico')
            .required('Il campo è obbligatorio')
        : yup.string().nullable(),
      verificationDate: isCensus
        ? yup.date().typeError('Data non valida').nullable().required('Il campo è obbligatorio')
        : yup.date().typeError('Data non valida').nullable(),
      censusReason: isCensus
        ? yup.object().nullable().required('Il campo è obbligatorio')
        : yup.object().nullable(),
    }),
    onSubmit,
  });

  const controlNotificationData = useCallback(
    (values: CancellationPracticeVM) => {
      const date = values.closureDate?.toFormat('yyyy-MM-dd') || '';
      const hours = values.closureTime?.toFormat('HH:mm:ss') || '';
      const timestampClosure =
        DateTime.fromFormat(`${date || ''} ${hours || ''}`, 'yyyy-MM-dd HH:mm:ss') || undefined;

      (
        checkNotificationControl({
          data: {
            codiceEnte: params.codiceEnte || undefined,
            tipoRegistro: params.tipoRegistro || undefined,
            idAnnoPratica: params.idAnnoPratica
              ? Number.parseInt(params.idAnnoPratica, 10)
              : undefined,
            progressivoPratica: params.progressivoPratica
              ? Number.parseInt(params.progressivoPratica, 10)
              : undefined,
            tsChiusura: timestampClosure,
            esito: '0', // 0 se chiusura negativa 1 chiusura positiva
          },
        }) as ResponsePromise<HttpResponse<{ showMessage: boolean; messaggio: string }>>
      ).then((response) => {
        if (response?.data.payload.showMessage) {
          setWarningModal({ open: true, warnings: response?.data.payload.messaggio });
        } else {
          formContext.handleSubmit();
        }
      });
    },
    [checkNotificationControl]
  );

  const cancelNotification = useCallback(
    (values: CancellationPracticeVM) => {
      const date = values.closureDate?.toFormat('yyyy-MM-dd') || '';
      const hours = values.closureTime?.toFormat('HH:mm:ss') || '';
      const timestampClosure =
        DateTime.fromFormat(`${date || ''} ${hours || ''}`, 'yyyy-MM-dd HH:mm:ss') || undefined;

      (
        cancelNotifications({
          data: {
            codiceEnte: params.codiceEnte || undefined,
            tipoRegistro: params.tipoRegistro || undefined,
            idAnnoPratica: params.idAnnoPratica
              ? Number.parseInt(params.idAnnoPratica, 10)
              : undefined,
            progressivoPratica: params.progressivoPratica
              ? Number.parseInt(params.progressivoPratica, 10)
              : undefined,
            tsChiusura: timestampClosure,
            esito: '0', // 0 se chiusura negativa 1 chiusura positiva
          },
        }) as ResponsePromise<void>
      ).then(() => {
        formContext.handleSubmit();
      });
    },
    [cancelNotifications]
  );

  const printNotifications = useCallback(
    (values: CancellationPracticeVM) => {
      const date = values.closureDate?.toFormat('yyyy-MM-dd') || '';
      const hours = values.closureTime?.toFormat('HH:mm:ss') || '';
      const timestampClosure =
        DateTime.fromFormat(`${date || ''} ${hours || ''}`, 'yyyy-MM-dd HH:mm:ss') || undefined;

      (
        printNotification({
          responseType: 'blob',
          data: {
            codiceEnte: params.codiceEnte || undefined,
            tipoRegistro: params.tipoRegistro || undefined,
            idAnnoPratica: params.idAnnoPratica
              ? Number.parseInt(params.idAnnoPratica, 10)
              : undefined,
            progressivoPratica: params.progressivoPratica
              ? Number.parseInt(params.progressivoPratica, 10)
              : undefined,
            tsChiusura: timestampClosure,
            esito: '0', // 0 se chiusura negativa 1 chiusura positiva
          },
        }) as ResponsePromise<void>
      ).then((response) => {
        if (response?.data) {
          const blob = new Blob([(response.data as string) || ''], { type: 'application/pdf' });

          setModalPrintSuccess({
            open: false,
          });

          blobToBase64(blob).then((base64) => {
            setPdfModal({
              data: base64.split(',')[1] as string,
              name: 'Stampa comunicazione',
              open: true,
            });
          });
        }
      });
    },
    [printNotification]
  );

  return (
    <Fragment>
      {warningModal.open ? (
        <ModalWarningMessage
          onYes={() => {
            printNotifications(formContext.values);
          }}
          onNo={() => {
            cancelNotification(formContext.values);
          }}
          open={warningModal.open}
          message={warningModal.warnings || ''}
          hasAnnulla
          onCloseAnnulla={() => {
            setWarningModal({ open: false });
          }}
        />
      ) : undefined}
      {pdfModal.open ? (
        <ModalViewerPDF
          open={pdfModal.open}
          data={pdfModal.data || ''}
          name={pdfModal.name || 'Stampa Notifica'}
          title={pdfModal.name || 'Stampa Notifica'}
          onClose={() => {
            setModalPrintSuccess((oldState) => ({
              ...oldState,
              data: pdfModal.data,
              open: true,
            }));
            setPdfModal({ data: '', open: false, name: '' });
          }}
        />
      ) : undefined}
      {modalPrintSuccess.open ? (
        <ModalPrintSuccess
          open={modalPrintSuccess.open}
          onYes={() => {
            setPdfModal({ open: false });
            formContext.handleSubmit();
          }}
          onClose={() => {
            setPdfModal({ name: '', data: modalPrintSuccess.data || '', open: true });
            setModalPrintSuccess({ open: false });
          }}
        />
      ) : undefined}
      <Accordion
        expanded={open}
        onChange={() => {
          setOpen(!open);
        }}
        ref={ref}
      >
        <AccordionSummary
          aria-controls="panel-positive-closure-practice-content"
          id="panel-positive-closure-practice-header"
          expandIcon={<ExpandMore />}
        >
          <Typography>Chiusura Pratica Negativa</Typography>
        </AccordionSummary>
        <AccordionDetails>
          <AccordionNegativeClosurePracticeForm
            values={formContext.values}
            onChange={formContext.setFieldValue}
            errors={formContext.errors}
            touched={formContext.touched}
            typeRegister={params.tipoRegistro as TypeRegister}
            hideReasonBox={hideReasonBox}
          />
          <div className="pmi-align-right pmi-pt-2 pmi-d-flex">
            <PmiButton
              color="cdmBrandThird"
              variant="outlined"
              onClick={() => {
                formContext.validateForm(formContext.values).then((errors) => {
                  if (Object.values(errors).length > 0) {
                    formContext.handleSubmit();
                    return;
                  }
                  if (params.tipoRegistro === TypeRegister.Immigration) {
                    controlNotificationData(formContext.values);
                  } else {
                    formContext.handleSubmit();
                  }
                });
              }}
              id="confirm"
            >
              Conferma
            </PmiButton>
            <div className="pmi-pl-3">
              <PmiButton
                color="cdmBrandThird"
                variant="outlined"
                onClick={() =>
                  hideReasonBox ? onPrintUnavailability() : onPrint(formContext.values)
                }
                id="print"
              >
                Stampa
              </PmiButton>
            </div>
          </div>
        </AccordionDetails>
      </Accordion>
      <ModalViewerPDF
        open={pdfModal.open}
        data={pdfModal.data || ''}
        name={pdfModal.name || ''}
        title="Stampa chiusura negativa"
        onClose={() => {
          setPdfModal({ data: '', open: false, name: '' });
        }}
      />
    </Fragment>
  );
};

AccordionNegativeClosurePracticeComponent.displayName =
  'Accordion Negative Closure Practice component';

const AccordionNegativeClosurenPractice = memo(AccordionNegativeClosurePracticeComponent);

export default AccordionNegativeClosurenPractice;
